import {Button} from "@heroui/button";
import {Link} from "@heroui/link";
import {
    Column,
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import React, {useEffect, useMemo, useRef, useState} from "react";

interface TwitchClip {
    id: string;
    url: string;
    embed_url: string;
    broadcaster_id: string;
    broadcaster_name: string;
    creator_id: string;
    creator_name: string;
    video_id: string;
    game_id: string;
    language: string;
    title: string;
    view_count: number;
    created_at: string;
    thumbnail_url: string;
    duration: number;
    vod_offset: number | null;
    is_featured: boolean;
}

export type TableClip = Omit<TwitchClip, "created_at"> & {
    created_at: Date;
    no: number;
};

type TwitchClipsTableProps = {
    clips: TwitchClip[];
    onSlugsChanged: (clips: TableClip[]) => void;
};

const dateFormatter = new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeZone: "UTC",
});

function DownloadIcon() {
    return (
        <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24">
            <path fill="currentColor" d="M5 20h14v-2H5zM19 9h-4V3H9v6H5l7 7z" />
        </svg>
    );
}

function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 300,
    ...props
}: {
    value: string | number;
    onChange: (value: string | number) => void;
    debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
    const [value, setValue] = useState(initialValue);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        const timeout = setTimeout(() => onChangeRef.current(value), debounce);

        return () => clearTimeout(timeout);
    }, [debounce, value]);

    return (
        <input
            {...props}
            value={value}
            onChange={(event) => setValue(event.target.value)}
        />
    );
}

function TitleFilter({column}: {column: Column<TableClip, unknown>}) {
    return (
        <DebouncedInput
            aria-label="Search clips by title"
            className="h-9 w-full min-w-48 rounded-lg border border-zinc-700 bg-zinc-950 px-3 text-sm font-normal normal-case text-white outline-none transition placeholder:text-zinc-500 focus:border-[#9146ff] focus:ring-2 focus:ring-[#9146ff]/20"
            onChange={(value) => column.setFilterValue(value)}
            placeholder="Search clips..."
            type="search"
            value={(column.getFilterValue() ?? "") as string}
        />
    );
}

function ViewCountFilter({column}: {column: Column<TableClip, unknown>}) {
    const filterValue = column.getFilterValue() as [string, string] | undefined;

    return (
        <div className="flex gap-2">
            <DebouncedInput
                aria-label="Minimum views"
                className="h-9 w-24 rounded-lg border border-zinc-700 bg-zinc-950 px-3 text-sm font-normal normal-case text-white outline-none transition placeholder:text-zinc-500 focus:border-[#9146ff] focus:ring-2 focus:ring-[#9146ff]/20"
                min="0"
                onChange={(value) => column.setFilterValue([value, filterValue?.[1] ?? ""])}
                placeholder="Min"
                type="number"
                value={filterValue?.[0] ?? ""}
            />
            <DebouncedInput
                aria-label="Maximum views"
                className="h-9 w-24 rounded-lg border border-zinc-700 bg-zinc-950 px-3 text-sm font-normal normal-case text-white outline-none transition placeholder:text-zinc-500 focus:border-[#9146ff] focus:ring-2 focus:ring-[#9146ff]/20"
                min="0"
                onChange={(value) => column.setFilterValue([filterValue?.[0] ?? "", value])}
                placeholder="Max"
                type="number"
                value={filterValue?.[1] ?? ""}
            />
        </div>
    );
}

function SortableHeader({
    column,
    children,
}: {
    column: Column<TableClip, unknown>;
    children: React.ReactNode;
}) {
    const direction = column.getIsSorted();

    return (
        <button
            className="flex items-center gap-2 text-left hover:text-white"
            onClick={column.getToggleSortingHandler()}
            type="button"
        >
            {children}
            {direction ? (
                <span className="text-[10px] text-[#bf94ff]">
                    {direction === "asc" ? "ASC" : "DESC"}
                </span>
            ) : null}
        </button>
    );
}

function PaginationButton({
    children,
    ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
        <button
            className="flex h-9 min-w-9 items-center justify-center rounded-lg border border-zinc-700 bg-zinc-900 px-3 text-sm font-medium text-zinc-200 transition hover:border-zinc-600 hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40"
            type="button"
            {...props}
        >
            {children}
        </button>
    );
}

export default function TwitchClipsTable({clips, onSlugsChanged}: TwitchClipsTableProps) {
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const data = useMemo<TableClip[]>(
        () => clips.map((clip, index) => ({
            ...clip,
            no: index + 1,
            created_at: new Date(clip.created_at),
        })),
        [clips],
    );

    const columns = useMemo<ColumnDef<TableClip, unknown>[]>(
        () => [
            {
                accessorKey: "no",
                enableColumnFilter: false,
                enableSorting: false,
                header: "#",
            },
            {
                accessorKey: "title",
                header: ({column}) => <SortableHeader column={column}>Clip</SortableHeader>,
                cell: ({row}) => (
                    <a
                        className="group/link flex min-w-72 items-center gap-3"
                        href={row.original.url}
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        <img
                            alt=""
                            className="aspect-video h-14 w-24 shrink-0 rounded-lg object-cover"
                            loading="lazy"
                            src={row.original.thumbnail_url}
                        />
                        <span className="min-w-0">
                            <span className="block max-w-md truncate font-medium text-white transition group-hover/link:text-[#bf94ff]">
                                {row.original.title}
                            </span>
                            <span className="mt-1 block text-xs text-zinc-400">
                                {row.original.creator_name ? `Clipped by ${row.original.creator_name} - ` : ""}
                                {dateFormatter.format(row.original.created_at)}
                            </span>
                        </span>
                    </a>
                ),
            },
            {
                accessorKey: "view_count",
                header: ({column}) => <SortableHeader column={column}>Views</SortableHeader>,
                filterFn: (row, columnId, value: [string, string]) => {
                    const views = row.getValue<number>(columnId);
                    const [minimum, maximum] = value ?? [];

                    if (minimum !== "" && views < Number(minimum)) return false;
                    if (maximum !== "" && views > Number(maximum)) return false;
                    return true;
                },
                cell: ({getValue}) => (
                    <span className="font-medium text-zinc-200">
                        {Number(getValue()).toLocaleString("en")}
                    </span>
                ),
            },
            {
                id: "download",
                enableColumnFilter: false,
                enableSorting: false,
                header: "Action",
                cell: ({row}) => (
                    <Button
                        as={Link}
                        className="bg-[#9146ff] font-medium text-white"
                        href={`https://twitch-vod.download/?download=${encodeURIComponent(row.original.url)}`}
                        rel="noopener noreferrer"
                        size="sm"
                        startContent={<DownloadIcon />}
                        target="_blank"
                    >
                        Download
                    </Button>
                ),
            },
        ],
        [],
    );

    const table = useReactTable({
        data,
        columns,
        state: {columnFilters},
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });
    const filteredRows = table.getFilteredRowModel().rows;

    useEffect(() => {
        onSlugsChanged(filteredRows.map((row) => row.original));
    }, [filteredRows, onSlugsChanged]);

    return (
        <section className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 shadow-xl shadow-black/10">
            <div className="flex flex-col gap-1 border-b border-zinc-800 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-white">Channel clips</h2>
                    <p className="text-sm text-zinc-400">
                        {filteredRows.length.toLocaleString("en")} of {data.length.toLocaleString("en")} clips selected
                    </p>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] border-collapse">
                    <thead className="bg-zinc-950/70">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th
                                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-400 first:w-14"
                                        colSpan={header.colSpan}
                                        key={header.id}
                                    >
                                        {header.isPlaceholder ? null : (
                                            <div className="space-y-2">
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {header.column.id === "title" ? (
                                                    <TitleFilter column={header.column} />
                                                ) : null}
                                                {header.column.id === "view_count" ? (
                                                    <ViewCountFilter column={header.column} />
                                                ) : null}
                                            </div>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.length ? (
                            table.getRowModel().rows.map((row) => (
                                <tr
                                    className="border-t border-zinc-800 transition-colors hover:bg-zinc-800/60"
                                    key={row.id}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td className="px-4 py-3 text-sm text-zinc-400" key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr className="border-t border-zinc-800">
                                <td className="px-6 py-12 text-center text-zinc-400" colSpan={columns.length}>
                                    No clips match these filters.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-col gap-4 border-t border-zinc-800 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                    <PaginationButton
                        aria-label="First page"
                        disabled={!table.getCanPreviousPage()}
                        onClick={() => table.setPageIndex(0)}
                    >
                        First
                    </PaginationButton>
                    <PaginationButton
                        aria-label="Previous page"
                        disabled={!table.getCanPreviousPage()}
                        onClick={() => table.previousPage()}
                    >
                        Prev
                    </PaginationButton>
                    <PaginationButton
                        aria-label="Next page"
                        disabled={!table.getCanNextPage()}
                        onClick={() => table.nextPage()}
                    >
                        Next
                    </PaginationButton>
                    <PaginationButton
                        aria-label="Last page"
                        disabled={!table.getCanNextPage()}
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    >
                        Last
                    </PaginationButton>
                </div>

                <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-400">
                    <span>
                        Page <strong className="text-white">{table.getState().pagination.pageIndex + 1}</strong>
                        {" of "}
                        <strong className="text-white">{Math.max(table.getPageCount(), 1)}</strong>
                    </span>
                    <label className="flex items-center gap-2">
                        Rows
                        <select
                            className="h-9 rounded-lg border border-zinc-700 bg-zinc-950 px-2 text-sm text-white outline-none focus:border-[#9146ff]"
                            onChange={(event) => table.setPageSize(Number(event.target.value))}
                            value={table.getState().pagination.pageSize}
                        >
                            {[10, 20, 30, 40, 50].map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    {pageSize}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            </div>
        </section>
    );
}
